<script setup lang="ts">
import { dollars } from '@nuts/auto-delivery-sdk/dist/utils/money';
import {
  breakpointsTailwind,
  useBreakpoints,
  useElementBounding,
  useElementVisibility,
  useIntersectionObserver,
  watchDebounced,
} from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { ComponentPublicInstance, computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { RouteLocationNormalized, useRoute, useRouter } from 'vue-router';
import { Store, useStore } from 'vuex';

import giftIcon from '@/assets/layout/nav/gift-icon.svg';
import AddedToAutoDeliveryModal from '@/components/auto-delivery/added-to-auto-delivery/AddedToAutoDeliveryModal.vue';
import AddCircularIcon from '@/components/base/assets/AddCircularIcon.vue';
import CartIcon from '@/components/base/assets/CartIcon.vue';
import CloseIcon from '@/components/base/assets/CloseIcon.vue';
import RemoveCircularIcon from '@/components/base/assets/RemoveCircularIcon.vue';
import ReorderIcon from '@/components/base/assets/ReorderIcon.vue';
import RoundedPersonIcon from '@/components/base/assets/RoundedPersonIcon.vue';
import SearchIcon from '@/components/base/assets/SearchIcon.vue';
import Collapse from '@/components/base/layout/Collapse.vue';
import Container from '@/components/base/layout/Container.vue';
import TheOverlay from '@/components/base/layout/TheOverlay.vue';
import RouteLink from '@/components/base/RouteLink.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import UnstyledButton from '@/components/base/UnstyledButton.vue';
import CartAddedModal from '@/components/cart/CartAddedModal.vue';
import CmsContent from '@/components/cms/CmsContent.vue';
import AccountDropDownMenu from '@/components/layout/header/AccountDropDownMenu.vue';
import ActionItemsButton from '@/components/layout/header/ActionItemsButton.vue';
import ActionItemsText from '@/components/layout/header/ActionItemsText.vue';
import AdminCartLookupForm from '@/components/layout/header/AdminCartLookupForm.vue';
import CampaignCarousel from '@/components/layout/header/CampaignCarousel.vue';
import HiddenLink from '@/components/layout/header/HiddenLink.vue';
import MainNav from '@/components/layout/header/MainNav.vue';
import MobileMenuNav from '@/components/layout/header/MobileMenuNav.vue';
import SearchBar from '@/components/layout/header/search/SearchBar.vue';
import SearchModal from '@/components/layout/header/search/SearchModal.vue';
import SecondaryNav from '@/components/layout/header/SecondaryNav.vue';
import TopShelf from '@/components/layout/header/TopShelf.vue';
import LoginModal from '@/components/login/LoginModal.vue';
import FTBPopup from '@/components/personalization/FTBPopup.vue';
import DetailShippingContent from '@/components/shipping/DetailShippingContent.vue';
import { useDyChooseResults } from '@/composables/dynamic-yield/experiences';
import { useRouteChange } from '@/composables/navigation/useRouteChange';
import { useAuth } from '@/composables/useAuth';
import { useCart } from '@/composables/useCart';
import { useCheckout } from '@/composables/useCheckout';
import { useCms } from '@/composables/useCms';
import { useDelivery } from '@/composables/useDelivery';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { useLoginModal } from '@/composables/useLoginModal';
import { useState } from '@/composables/useState';
import { staticAccountLinks } from '@/data/nav/accountLinks';
import { staticMegaMenu } from '@/data/nav/megaMenu';
import { staticMobileMenu } from '@/data/nav/mobileNav';
import { SecondaryNavLink } from '@/data/nav/secondaryNav';
import { JsonChoice, JsonVariation } from '@/lib/personalization/dynamicYield';
import { useAutoDelivery } from '@/stores/autoDelivery';
import { useCartDiscounts } from '@/stores/cartDiscounts';
import { useCriticalData } from '@/stores/criticalData';
import { useNotifications } from '@/stores/notifications';
import { useSession } from '@/stores/session';
import { DataModel, getVariationOrControl, GlobalCampaignCarousel } from '@/utils/cms';
import { MegaMenu, MobileNav, SiteNavigation, SiteNavigationCategories } from '@/utils/navMenu';
import { adaptMultipleQueryValues } from '@/utils/search/createAlgoliaRouter';
import { waitABit } from '@/utils/waitABit';

const route = useRoute();
const store = useStore();

const { permissions } = useAuth(store);
const autoDeliveryStore = useAutoDelivery();
const { addedToAutoDelivery, addedToAutoDeliveryOrder, addedToAutoDeliverySubscription } =
  storeToRefs(autoDeliveryStore);
const {
  isCartAddedOpen,
  loadCart,
  loadLineItemExpansions,
  preCheckoutLineItems: lineItems,
} = useCart(store);
const { freeShippingThreshold } = useDelivery(store);
const { flags } = useFeatureFlags();
const { handleClose, handleOpen } = useLoginModal();
const { navigateTo } = useRouteChange(useRouter());

const { cxMode } = useCheckout(store);
const session = useSession();
const { userCreatedAt, userFirstName } = storeToRefs(session);
const isSignedIn = computed(() => userCreatedAt.value || cxMode.value);

const requireUser = (destination: string) => {
  if (isSignedIn.value) {
    navigateTo(destination);
  } else {
    handleOpen({ destination });
  }
};

const isSearchPage = computed(() => route?.name === 'Search');
const { query } = useState('synchronizedSearchBar', () => ({ query: '' }));
if (isSearchPage.value) {
  query.value = route.query.query ? adaptMultipleQueryValues(route.query) : '';
}

const showMobileMenu = ref(false);
const showShippingDetails = ref(false);

const topShelfRef = ref<ComponentPublicInstance>();
const mainNavRef = ref<ComponentPublicInstance>();
const desktopMenuVisible = useElementVisibility(mainNavRef);
const { content: desktopMenu } = useCms<DataModel<MegaMenu>>('mega-menu', {
  contentVisible: () => desktopMenuVisible.value,
  prefetch: 'setup',
});
const desktopSiteNavigation = computed<SiteNavigation>(() => ({
  accountLinks: staticAccountLinks,
  categories: SiteNavigationCategories.fromMegaMenu(
    getVariationOrControl<MegaMenu>(
      desktopMenu.value.result,
      desktopMenu.value.prefetchedContentTestDecision,
    )?.data ?? staticMegaMenu,
    flags.seasonalGiftsIcon ? giftIcon : undefined,
  ),
}));

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('lg');

const announcementBarRef = ref<HTMLElement>();
const mastheadContents = ref<HTMLElement>();

const { height: mastheadContentsHeight } = useElementBounding(mastheadContents);

const mobileMenuOffset = computed(() => {
  if (!mastheadContentsHeight.value) return '5.75rem';
  return `${mastheadContentsHeight.value}px`;
});

const { content: mobileMenu } = useCms<DataModel<MobileNav>>('mobile-menu', {
  contentVisible: () => showMobileMenu.value,
  prefetch: 'setup',
});
const mobileSiteNavigation = computed<SiteNavigation>(() => ({
  accountLinks: staticAccountLinks,
  categories: SiteNavigationCategories.fromMobileNav(
    getVariationOrControl<MobileNav>(
      mobileMenu.value.result,
      mobileMenu.value.prefetchedContentTestDecision,
    )?.data ?? staticMobileMenu,
    flags.seasonalGiftsIcon ? giftIcon : undefined,
  ),
}));

const resetState = () => {
  showShippingDetails.value = false;
  showMobileMenu.value = false;
};

const onCartClick = () => {
  resetState();
  navigateTo('/cart');
};

const onMenuClick = () => {
  const current = showMobileMenu.value;
  resetState();
  showMobileMenu.value = !current;
};

const displaySearchSuggestions = ref(false);
const onSearchClick = async () => {
  if (isMobile.value) {
    if (isSearchPage.value) {
      const selector = breakpoints.isGreaterOrEqual('md')
        ? '#ais-search-box-desktop input'
        : '#search-input-mobile input';
      const inputField = document.querySelector(selector);
      if (inputField instanceof HTMLInputElement) {
        inputField.focus();
        inputField.selectionStart = query.value.length;
        inputField.selectionEnd = query.value.length;
        return;
      }
    }
  }

  if (isMobile.value && !isSearchPage.value) {
    displaySearchSuggestions.value = true;
    showMobileMenu.value = false;
  } else {
    resetState();
  }
};

const onShippingDetailsClick = async () => {
  const current = showShippingDetails.value;
  resetState();
  showShippingDetails.value = !current;

  if (isMobile.value && showShippingDetails.value) {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

type SecondaryNavVariation = JsonVariation<{ items: SecondaryNavLink[] }>;
interface CriticalData {
  choicesByName?: Record<string, JsonChoice<SecondaryNavVariation> | undefined>;
}
const critical = useCriticalData<CriticalData>('BaseHeader');
const secondaryNavSelector = '[NAV] Secondary Nav';
const { choicesByName, loadExperiences } = useDyChooseResults<JsonChoice<SecondaryNavVariation>>(
  secondaryNavSelector,
  route,
  {
    preloadedChoices: critical.data.choicesByName,
  },
);
const dynamicSecondaryNavLinks = computed(
  () => choicesByName[secondaryNavSelector]?.variations[0]?.payload.data.items,
);

onMounted(() => {
  waitABit().then(async () => {
    await loadCart();
    await loadLineItemExpansions();
  });

  watch(isMobile, (val) => {
    displaySearchSuggestions.value = false;

    if (!val) {
      showMobileMenu.value = false;
    }
  });

  loadExperiences({ skipLoadedExperiences: true });
});

watch(
  () => route?.path,
  () => {
    showMobileMenu.value = false;
  },
);

defineOptions({
  async criticalData(forwardedStore: Store<any>, to: RouteLocationNormalized) {
    const prefetch = 'criticalData';
    const promises: Promise<any>[] = [
      useCms('sitewide-banner', { prefetch }).loadCmsContent(to.path, to.query),
      useCms('promotional-messaging-bar', { prefetch }).loadCmsContent(to.path, to.query),
    ];

    if (typeof window === 'undefined') {
      const criticalDY =
        useDyChooseResults<JsonChoice<SecondaryNavVariation>>('[NAV] Secondary Nav');
      const criticalMegaMenu = useCms<DataModel<MegaMenu>>('mega-menu', { prefetch });
      const criticalMobileMenu = useCms<DataModel<MobileNav>>('mobile-menu', { prefetch });
      promises.push(
        criticalMegaMenu.loadCmsContent(to.path, to.query).then(() => {
          const decision = getVariationOrControl(criticalMegaMenu.content.value.result);
          if (decision?.id) {
            criticalMegaMenu.commitTestDecision(decision.id);
          }
        }),
        criticalMobileMenu.loadCmsContent(to.path, to.query).then(() => {
          const decision = getVariationOrControl(criticalMobileMenu.content.value.result);
          if (decision?.id) {
            criticalMobileMenu.commitTestDecision(decision.id);
          }
        }),
        criticalDY.loadExperiences().then(() => {
          useCriticalData<CriticalData>('BaseHeader').data.choicesByName = criticalDY.choicesByName;
        }),
      );
    }

    await Promise.all(promises);
  },
});

onBeforeUnmount(() => {
  handleClose();
  isCartAddedOpen.value = false;
});

const getDataPromo = (name: string) => ({
  'data-promo': '1',
  'data-promo-creative': 'Header Links',
  'data-promo-name': name,
});

const hasShadow = ref(false);
const promotionalMessagingBar = ref<ComponentPublicInstance>();
const promotionalMessagingBarVisible = useElementVisibility(promotionalMessagingBar);
const { content: promotionBanner } = useCms('promotional-messaging-bar', {
  contentVisible: () => promotionalMessagingBarVisible.value,
  prefetch: 'setup',
});

useIntersectionObserver(
  promotionalMessagingBar,
  ([{ isIntersecting, intersectionRatio }]) => {
    hasShadow.value = !isIntersecting || intersectionRatio < 1;
  },
  {
    threshold: [1],
  },
);

const scrolledScreen = ref(false);
useIntersectionObserver(topShelfRef, ([{ isIntersecting }]) => {
  scrolledScreen.value = !isIntersecting;
});
const showNavOnStickHeader = ref(true);
const userToggledMenu = ref(false);
const shouldShowCloseIcon = computed(() => showNavOnStickHeader.value && userToggledMenu.value);
watchDebounced(
  scrolledScreen,
  (val) => {
    showNavOnStickHeader.value = !val;
    if (val) {
      userToggledMenu.value = false;
      showShippingDetails.value = false;
    }
  },
  { debounce: 400 },
);

function toggleMenu() {
  showNavOnStickHeader.value = !showNavOnStickHeader.value;
  userToggledMenu.value = true;
}

const scrolledPastMainNav = ref(false);
useIntersectionObserver(mainNavRef, ([{ isIntersecting }]) => {
  scrolledPastMainNav.value = !isIntersecting;
});

const { content: campaignCarouselContent } = useCms<DataModel<GlobalCampaignCarousel>>(
  'global-campaign-carousel',
  {
    loadOnMount: true,
  },
);

const sitewideBannerRef = ref<ComponentPublicInstance>();
const siteWideBannerVisible = useElementVisibility(sitewideBannerRef);
const { content: sitewideBanner } = useCms('sitewide-banner', {
  contentVisible: () => siteWideBannerVisible.value,
  prefetch: 'setup',
});

const { setNotification } = useNotifications();
const { cartConditionsMetMessage } = storeToRefs(useCartDiscounts());
watch(cartConditionsMetMessage, (message) => {
  setNotification({ key: 'couponMessage', message, messageType: 'notices' });
});
</script>

<template>
  <div id="appHeader" class="sticky top-0 z-20 bg-white lg:relative lg:shadow">
    <HiddenLink to="#skiptocontent" class="lg:top-10">Skip to main content</HiddenLink>
    <HiddenLink to="https://nuts.com/accessibility.html" class="lg:top-10">
      Accessibility policy page
    </HiddenLink>

    <AdminCartLookupForm v-if="permissions.checkOutAsCustomer" />
    <CmsContent
      class="hidden lg:visible lg:block"
      :content="promotionBanner"
      model="promotional-messaging-bar"
      ref="promotionalMessagingBar"
    />
    <TopShelf
      class="hidden lg:visible lg:block"
      :campaigns="campaignCarouselContent.result?.data?.items"
      :class="{ shadow: hasShadow }"
      :isCarouselLoading="campaignCarouselContent.loading"
      ref="topShelfRef"
      @toggleShippingDetails="onShippingDetailsClick"
    />
    <div
      class="top-0 right-0 left-0 bg-white lg:transition-all lg:duration-300 lg:ease-in-out lg:z-20"
      :class="{
        'lg:fixed lg:shadow-md': scrolledScreen,
      }"
    >
      <Container class="relative lg:pb-0" noMobilePadding data-test="masthead">
        <div
          class="flex relative right-0 left-0 z-30 flex-col grid-cols-12 bg-white shadow lg:shadow-none lg:static lg:gap-4 lg:grid grow-0"
          :class="scrolledScreen ? 'lg:py-2' : 'lg:py-4'"
          data-test="mobile-header"
          ref="mastheadContents"
        >
          <CmsContent
            class="lg:hidden"
            :content="promotionBanner"
            model="promotional-messaging-bar"
            ref="promotionalMessagingBar"
          />
          <div
            class="flex order-2 col-span-3 gap-x-3 justify-between px-1 py-2 lg:justify-start lg:p-0 lg:order-none"
          >
            <div class="flex lg:hidden">
              <ActionItemsButton @click="onMenuClick">
                <div
                  class="flex flex-col gap-1 justify-center items-center"
                  data-test="hamburger-menu"
                >
                  <img
                    v-if="!showMobileMenu"
                    alt=""
                    class="object-contain w-6 h-6"
                    src="@/assets/menu-burger.svg"
                  />
                  <CloseIcon
                    v-else
                    data-promo="1"
                    data-promo-creative="Mobile Menu Nav Links"
                    data-promo-name="Close"
                    data-test="close-hamburger-menu"
                    :size="24"
                  />
                  <ActionItemsText>{{ showMobileMenu ? 'Close' : 'Shop' }}</ActionItemsText>
                </div>
              </ActionItemsButton>
              <div class="w-12 lg:hidden">
                <ActionItemsButton @click="requireUser('/account')">
                  <div class="flex flex-col gap-1 justify-center items-center">
                    <RoundedPersonIcon class="text-zinc-800" :size="24" />
                    <ActionItemsText>
                      {{ isSignedIn ? 'Account' : 'Sign in' }}
                    </ActionItemsText>
                  </div>
                </ActionItemsButton>
              </div>
            </div>
            <div
              class="hidden mr-2 w-14 transition-transform duration-300 ease-in-out lg:visible lg:block"
              :class="scrolledScreen ? 'lg:translate-x-0 ' : 'lg:-translate-x-8 '"
            >
              <UnstyledButton
                v-if="scrolledScreen"
                :aria-label="showNavOnStickHeader ? 'Close menu' : 'Open menu'"
                class="flex flex-col gap-1 justify-center items-center w-14 h-full rounded-full hover:bg-nuts-stone-100"
                @click="toggleMenu"
              >
                <img
                  v-if="!shouldShowCloseIcon"
                  alt=""
                  class="object-contain w-8 h-8"
                  src="@/assets/menu-burger.svg"
                />
                <CloseIcon v-else :size="32" />
              </UnstyledButton>
            </div>
            <RouteLink
              v-bind="getDataPromo('Site logo')"
              class="inline-flex gap-x-2 self-center transition-transform duration-300 ease-in-out"
              :class="scrolledScreen ? 'lg:translate-x-0' : 'lg:-translate-x-12'"
              to="/"
            >
              <span class="shrink">
                <img
                  alt="Nuts.com"
                  data-test="nuts-logo"
                  class="hidden object-contain transition-all duration-300 ease-in-out lg:visible lg:block"
                  :class="scrolledScreen ? 'h-11' : 'h-12'"
                  itemprop="logo"
                  src="@/assets/nuts-logo-text-only.svg"
                />
                <img
                  alt="Nuts.com"
                  data-test="nuts-logo-mobile"
                  class="block object-contain visible h-10 lg:hidden"
                  itemprop="logo"
                  src="@/assets/nuts-logo-text-only.svg"
                />
              </span>
              <span class="hidden shrink lg:visible lg:block" data-test="family-logo">
                <img
                  alt=""
                  class="object-contain"
                  :class="scrolledScreen ? 'h-11' : 'h-12'"
                  src="@/assets/nuts-family-redesign.png"
                />
              </span>
            </RouteLink>
            <div class="flex lg:hidden">
              <ActionItemsButton
                v-bind="getDataPromo('Search Icon')"
                data-test="search"
                @click="onSearchClick"
              >
                <div class="flex flex-col gap-1 justify-center items-center">
                  <SearchIcon :size="24" />
                  <ActionItemsText>Search</ActionItemsText>
                </div>
              </ActionItemsButton>
              <ActionItemsButton
                v-bind="getDataPromo('Cart')"
                data-test="cart"
                @click="onCartClick"
              >
                <div class="flex flex-col gap-1 justify-center items-center lg:hidden">
                  <div class="flex relative">
                    <img alt="" class="object-contain w-6 h-6" src="@/assets/shopping-cart.svg" />
                    <div
                      v-show="lineItems.length"
                      aria-hidden="true"
                      class="flex absolute -top-1 -right-1 justify-center items-center w-4 h-4 rounded-full shrink-0 bg-nuts-amber-400"
                    >
                      <span class="text-xs font-semibold leading-tight text-center text-black">
                        {{ lineItems.length }}
                      </span>
                    </div>
                  </div>
                  <ActionItemsText>
                    <span class="sr-only">{{ lineItems.length }} items in your</span>Cart
                  </ActionItemsText>
                </div>
              </ActionItemsButton>
            </div>
          </div>
          <div
            class="hidden col-span-5 self-center lg:visible lg:block lg:mx-0 xl:col-span-6 xl:mx-10"
          >
            <SearchBar
              v-model:isOpen="displaySearchSuggestions"
              v-model:query="query"
              v-bind="getDataPromo('Search bar')"
            />
          </div>
          <div
            class="hidden col-span-4 gap-2 xl:col-span-3 lg:order-none lg:flex lg:visible lg:items-center lg:justify-end"
          >
            <div v-if="isSignedIn" class="sr-only">
              Use space button to open the account navigation menu
            </div>

            <AccountDropDownMenu
              data-test="user"
              :userCreatedAt
              :userFirstName
              @goToAccount="requireUser('/account')"
            />
            <UnstyledButton
              v-bind="getDataPromo('Easy Reorder')"
              class="flex gap-2 items-center px-2 py-1 font-semibold rounded-full hover:bg-nuts-stone-100"
              @click.prevent="requireUser('/account/easy-reorder')"
            >
              <ReorderIcon class="text-zinc-800" :size="32" />
              <SmallBodyText> Reorder </SmallBodyText>
            </UnstyledButton>
            <div class="border-l border-solid border-neutral-300">
              <UnstyledButton
                class="flex items-center px-2 py-1 ml-2 font-semibold rounded-full hover:bg-nuts-stone-100"
                @click="navigateTo('/cart')"
              >
                <CartIcon class="text-zinc-800" data-test="cart-image" :size="32" />
                <span
                  v-if="lineItems.length"
                  class="px-1.5 rounded-xl text-tiny -ml-4 min-w-[1.25rem] text-black bg-nuts-amber-400 relative -top-3 -left-1"
                >
                  <span aria-hidden="true" class="text-xs font-semibold leading-tight text-center">
                    {{ lineItems.length }}
                  </span>
                </span>
                <span class="sr-only">{{ lineItems.length }} items in your Cart</span>
              </UnstyledButton>
            </div>
          </div>
          <CampaignCarousel
            class="lg:hidden"
            :items="campaignCarouselContent.result?.data?.items"
            :isLoading="campaignCarouselContent.loading"
          >
            <div class="flex flex-col justify-center items-center h-full" ref="announcementBarRef">
              <UnstyledButton
                v-bind="getDataPromo('Free Shipping message banner')"
                data-test="free-shipping-banner"
                aria-controls="free-shipping-details"
                :aria-expanded="showShippingDetails"
                aria-label="shipping details"
                class="inline-flex gap-x-1 items-center"
                @click="onShippingDetailsClick"
              >
                <SmallBodyText class="py-1 font-semibold leading-5 whitespace-nowrap">
                  FREE shipping on orders over ${{ dollars(freeShippingThreshold) }}!
                </SmallBodyText>
                <RemoveCircularIcon v-if="showShippingDetails" :size="16" />
                <AddCircularIcon v-else :size="16" />
              </UnstyledButton>
            </div>
          </CampaignCarousel>
        </div>
      </Container>
      <hr
        class="hidden m-0 h-px opacity-50 lg:visible lg:block bg-neutral-300"
        :class="{ 'lg:hidden': scrolledScreen }"
      />
      <Collapse :expanded="showShippingDetails">
        <div class="bg-nuts-amber-200" id="free-shipping-details">
          <Container>
            <DetailShippingContent
              class="flex justify-between items-center py-2 text-sm text-center"
              :freeShippingThreshold="freeShippingThreshold"
            />
          </Container>
        </div>
      </Collapse>
      <Collapse :expanded="showNavOnStickHeader">
        <MainNav
          class="hidden lg:visible lg:block"
          :siteNavigation="desktopSiteNavigation"
          ref="mainNavRef"
        />
        <SecondaryNav
          class="hidden lg:visible lg:block"
          :dynamicNavLinks="dynamicSecondaryNavLinks"
        />
      </Collapse>
    </div>
    <transition name="slide-from-left">
      <MobileMenuNav
        v-if="showMobileMenu"
        class="z-20 mobile-header-offset lg:hidden"
        :siteNavigation="mobileSiteNavigation"
        @handle-close="showMobileMenu = false"
      />
    </transition>
  </div>
  <CartAddedModal :isOpen="isCartAddedOpen" @handle-close="isCartAddedOpen = false" />
  <AddedToAutoDeliveryModal
    :delivery="addedToAutoDeliveryOrder"
    :isOpen="addedToAutoDelivery.isOpen"
    :subscription="addedToAutoDeliverySubscription"
    @handle-close="autoDeliveryStore.closeAddedToAutoDelivery"
  />
  <FTBPopup />
  <Teleport to="#teleported">
    <LoginModal />
  </Teleport>
  <transition name="fade" :duration="200">
    <SearchModal
      v-model:isOpen="displaySearchSuggestions"
      v-model:query="query"
      class="search-modal-offset"
    />
  </transition>
  <div v-show="scrolledScreen" class="desktop-header-offset" />
  <span id="skiptocontent" class="sr-only">Main Content</span>
  <CmsContent :content="sitewideBanner" model="sitewide-banner" ref="sitewideBannerRef" />
  <TheOverlay class="z-10" />
</template>

<style lang="scss" scoped>
.desktop-header-offset {
  @apply hidden lg:block;
  height: v-bind('`${mastheadContentsHeight}px`');
}
.mobile-header-offset {
  top: v-bind('mobileMenuOffset');
}

.search-modal-offset {
  top: 0;
  min-height: v-bind('`${mastheadContentsHeight}px`');
}

.slide-from-left-enter-active,
.slide-from-left-leave-active {
  transition: all 0.3s ease-in-out;
}

.slide-from-left-enter-from,
.slide-from-left-leave-to {
  @apply -translate-x-full md:-translate-x-96;
}
</style>
